// ============================================================
// BERLIN AMATOR LIG TAKIMLARI
//
// Mobil uygulamadaki "Spor > Berlin" bolumunde hangi kuluplerin
// gosterilecegi buradan yonetilir. Onceden liste uygulamanin
// icinde sabitti; degistirmek icin yeni surum cikmak gerekiyordu.
//
// KULUP KIMLIGI (Takim_Kulup_ID) NASIL BULUNUR:
//   1) https://www.fussball.de adresinde kulubu aratin,
//   2) kulup sayfasini acin,
//   3) adres cubugundaki  /-/id/  ifadesinden SONRAKI kodu kopyalayin.
//      Ornek: .../verein/anadoluspor-berlin/-/id/00ES8GNCAC00006HVV0AG08LVUPGND5I
//                                                 ^^^^^^^^^^ bu kisim
//
// Kulup kimlikleri sezondan sezona degismez; takim ve grup bilgisi
// mobil tarafta calisma aninda cozulur.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button, Card, CardBody, Col, Container, Form, FormFeedback, Input,
  Label, Modal, ModalBody, ModalHeader, Row, Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import { toastSuccess, toastError } from "helpers/toast";
import { ODATA_BASE } from "helpers/api_config";

import SayfaRehberi from "../../../components/Common/SayfaRehberi";
const TABLO = `${ODATA_BASE}/Berlin_Amator_Takim`;

// fussball.de kulup kimlikleri 32 haneli, buyuk harf + rakam.
const KULUP_ID_KALIBI = /^[A-Z0-9]{20,40}$/;

const BerlinAmatorLig = () => {
  document.title = "Berlin Amatör Lig | Berlin Esnaf Admin Paneli";

  const [kayitlar, setKayitlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [tabloYok, setTabloYok] = useState(false);
  const [modal, setModal] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const [silmeModal, setSilmeModal] = useState(false);

  const _getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      const res = await axios.get(TABLO, { params: { $orderby: "Takim_Sira asc" } });
      setKayitlar((res.data && res.data.value) || []);
      setTabloYok(false);
    } catch (e) {
      // Tablo henuz olusturulmadiysa OData 404 doner.
      if (e && e.response && e.response.status === 404) setTabloYok(true);
      else toastError("Takım listesi alınamadı.");
      setKayitlar([]);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { _getir(); }, [_getir]);

  const toggle = () => {
    setModal(m => {
      if (m) setDuzenlenen(null);
      return !m;
    });
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      Takim_Ad: (duzenlenen && duzenlenen.Takim_Ad) || "",
      Takim_Kulup_ID: (duzenlenen && duzenlenen.Takim_Kulup_ID) || "",
      Takim_Sira: duzenlenen && duzenlenen.Takim_Sira != null ? duzenlenen.Takim_Sira : kayitlar.length + 1,
      Takim_Aktif: duzenlenen ? duzenlenen.Takim_Aktif !== false : true,
    },
    validationSchema: Yup.object({
      Takim_Ad: Yup.string().trim().required("Lütfen takım adı giriniz").max(80, "En fazla 80 karakter"),
      Takim_Kulup_ID: Yup.string()
        .trim()
        .required("Lütfen fussball.de kulüp kimliğini giriniz")
        .matches(KULUP_ID_KALIBI, "Kulüp kimliği yalnızca büyük harf ve rakamlardan oluşur (örn. 00ES8GNCAC00006HVV0AG08LVUPGND5I)"),
      Takim_Sira: Yup.number().typeError("Sıra sayı olmalı").min(0, "Sıra 0'dan küçük olamaz").required("Lütfen sıra giriniz"),
    }),
    onSubmit: async values => {
      const govde = {
        Takim_Ad: values.Takim_Ad.trim(),
        Takim_Kulup_ID: values.Takim_Kulup_ID.trim(),
        Takim_Sira: parseInt(values.Takim_Sira, 10),
        Takim_Aktif: !!values.Takim_Aktif,
      };
      try {
        if (duzenlenen) await axios.patch(`${TABLO}(${duzenlenen.ID})`, govde);
        else await axios.post(TABLO, govde);
        toastSuccess(duzenlenen ? "Takım güncellendi." : "Takım eklendi.");
        toggle();
        validation.resetForm();
        _getir();
      } catch (e) {
        toastError("Kayıt işlemi başarısız oldu.");
      }
    },
  });

  const _duzenle = kayit => { setDuzenlenen(kayit); setModal(true); };

  const _silmeOnayi = async () => {
    if (!silinecek) return;
    try {
      await axios.delete(`${TABLO}(${silinecek.ID})`);
      toastSuccess("Takım silindi.");
    } catch (e) {
      toastError("Takım silinemedi.");
    }
    // Secili satir listeyi ezmesin diye once temizleniyor.
    setSilinecek(null);
    setSilmeModal(false);
    _getir();
  };

  const _aktifDegistir = async kayit => {
    try {
      await axios.patch(`${TABLO}(${kayit.ID})`, { Takim_Aktif: !kayit.Takim_Aktif });
      _getir();
    } catch (e) {
      toastError("Durum değiştirilemedi.");
    }
  };

  const aktifSayisi = useMemo(
    () => kayitlar.filter(k => k.Takim_Aktif !== false).length,
    [kayitlar],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={silmeModal}
        onDeleteClick={_silmeOnayi}
        onCloseClick={() => { setSilmeModal(false); setSilinecek(null); }}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Spor" breadcrumbItem="Berlin Amatör Lig Takımları" />
          <SayfaRehberi sayfa="berlinamatorlig" />

          {tabloYok ? (
            <Card>
              <CardBody>
                {/* ONCEKI METIN YANILTICIYDI: yalnizca "tablo olusturulmamis"
                    diyordu. Oysa asil sebep genelde SQL'in calismamis olmasi
                    degil, API katmaninin guncellenmemis olmasidir. Bu OData
                    servisi elle bakimli bir EDMX uzerinden calisiyor; tabloyu
                    olusturmak TEK BASINA yetmiyor. (2026-09-01'de tam olarak
                    bu yasandi: tablo 15 kayitla veritabanindaydi ama
                    /Berlin_Amator_Takim ucu 404 donuyordu.) */}
                <h5 className="text-danger">Takım listesi API'den okunamadı</h5>
                <p className="mb-2">
                  Bu sayfa <code>Berlin_Amator_Takim</code> tablosunu kullanıyor, ancak
                  API bu adrese <strong>HTTP 404</strong> dönüyor. İki olasılık var:
                </p>
                <ol className="mb-2">
                  <li>
                    Tablo henüz oluşturulmamış — gereken SQL mobil projedeki{" "}
                    <code>veritabani/berlin_amator_takim.sql</code> dosyasındadır.
                  </li>
                  <li>
                    <strong>Tablo var ama API'ye tanıtılmamış.</strong> SQL'i
                    çalıştırdıysanız sebep büyük olasılıkla budur: OData modeline
                    (EDMX) eklenmesi, controller'ının projeye konması ve
                    WebApiConfig'e kaydedilmesi gerekir. Adımlar{" "}
                    <code>BesApi/DEPLOY-BERLIN-AMATOR-TAKIM.md</code> dosyasında.
                  </li>
                </ol>
                <p className="mb-0 text-muted">
                  Bu süre boyunca mobil uygulama kendi içindeki hazır listeyi
                  gösterir; Berlin Amatör Lig bölümü kullanıcı tarafında
                  çalışmaya devam eder. Etkilenen tek şey panelden takım
                  yönetimidir.
                </p>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardBody>
              <Row className="mb-3 align-items-center">
                <Col sm="8">
                  <p className="text-muted mb-0">
                    Mobil uygulamada <strong>Spor &gt; Berlin</strong> sekmesinde gösterilecek kulüpler.
                    Şu an <strong>{aktifSayisi}</strong> kulüp aktif.
                  </p>
                </Col>
                <Col sm="4" className="text-sm-end">
                  <Button color="success" onClick={() => { setDuzenlenen(null); setModal(true); }} disabled={tabloYok}>
                    <i className="mdi mdi-plus me-1" /> Takım Ekle
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table className="table align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 70 }}>Sıra</th>
                      <th>Takım Adı</th>
                      <th>fussball.de Kulüp Kimliği</th>
                      <th style={{ width: 110 }}>Durum</th>
                      <th style={{ width: 130 }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yukleniyor ? (
                      <tr><td colSpan="5" className="text-center py-4">Yükleniyor...</td></tr>
                    ) : kayitlar.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-4 text-muted">Kayıtlı takım yok.</td></tr>
                    ) : (
                      kayitlar.map(kayit => (
                        <tr key={kayit.ID}>
                          <td>{kayit.Takim_Sira}</td>
                          <td>{kayit.Takim_Ad}</td>
                          <td>
                            <code style={{ fontSize: 11 }}>{kayit.Takim_Kulup_ID}</code>
                            <a
                              className="ms-2"
                              href={`https://www.fussball.de/verein/-/-/id/${kayit.Takim_Kulup_ID}`}
                              target="_blank"
                              rel="noreferrer"
                              title="fussball.de'de aç"
                            >
                              <i className="mdi mdi-open-in-new" />
                            </a>
                          </td>
                          <td>
                            <Button
                              size="sm"
                              color={kayit.Takim_Aktif !== false ? "success" : "secondary"}
                              onClick={() => _aktifDegistir(kayit)}
                            >
                              {kayit.Takim_Aktif !== false ? "Aktif" : "Pasif"}
                            </Button>
                          </td>
                          <td>
                            <Link to="#" className="text-success me-3" onClick={() => _duzenle(kayit)}>
                              <i className="mdi mdi-pencil font-size-18" />
                            </Link>
                            <Link
                              to="#"
                              className="text-danger"
                              onClick={() => { setSilinecek(kayit); setSilmeModal(true); }}
                            >
                              <i className="mdi mdi-delete font-size-18" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>

          <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader toggle={toggle} tag="h4">
              {duzenlenen ? "Takımı Düzenle" : "Takım Ekle"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={e => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                <div className="mb-3">
                  <Label className="form-label">Takım Adı</Label>
                  <Input
                    name="Takim_Ad"
                    type="text"
                    placeholder="Örn: Türkiyemspor Berlin"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.Takim_Ad || ""}
                    invalid={!!(validation.touched.Takim_Ad && validation.errors.Takim_Ad)}
                  />
                  {validation.touched.Takim_Ad && validation.errors.Takim_Ad ? (
                    <FormFeedback type="invalid">{validation.errors.Takim_Ad}</FormFeedback>
                  ) : null}
                </div>

                <div className="mb-3">
                  <Label className="form-label">fussball.de Kulüp Kimliği</Label>
                  <Input
                    name="Takim_Kulup_ID"
                    type="text"
                    placeholder="00ES8GNCAC00006HVV0AG08LVUPGND5I"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.Takim_Kulup_ID || ""}
                    invalid={!!(validation.touched.Takim_Kulup_ID && validation.errors.Takim_Kulup_ID)}
                  />
                  {validation.touched.Takim_Kulup_ID && validation.errors.Takim_Kulup_ID ? (
                    <FormFeedback type="invalid">{validation.errors.Takim_Kulup_ID}</FormFeedback>
                  ) : (
                    <small className="text-muted">
                      fussball.de&apos;de kulüp sayfasını açın; adresteki <code>/-/id/</code> ifadesinden
                      sonraki kodu buraya yapıştırın.
                    </small>
                  )}
                </div>

                <div className="mb-3">
                  <Label className="form-label">Sıra</Label>
                  <Input
                    name="Takim_Sira"
                    type="number"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.Takim_Sira}
                    invalid={!!(validation.touched.Takim_Sira && validation.errors.Takim_Sira)}
                  />
                  {validation.touched.Takim_Sira && validation.errors.Takim_Sira ? (
                    <FormFeedback type="invalid">{validation.errors.Takim_Sira}</FormFeedback>
                  ) : (
                    <small className="text-muted">Küçük sayı önce gösterilir.</small>
                  )}
                </div>

                <div className="mb-3 form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="takimAktif"
                    name="Takim_Aktif"
                    checked={!!validation.values.Takim_Aktif}
                    onChange={validation.handleChange}
                  />
                  <Label className="form-check-label" htmlFor="takimAktif">
                    Uygulamada göster
                  </Label>
                </div>

                <Row>
                  <Col>
                    <div className="text-end">
                      <button type="submit" className="btn btn-success save-user">Kaydet</button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default BerlinAmatorLig;
